<script lang="ts">
	/**
	 * The one filter set, driving every panel on the page (§12.9, §12.10).
	 *
	 * One bar rather than a control per panel, and that is not a layout preference: a dashboard
	 * whose panels each read their own filters is a dashboard whose panels disagree about what is on
	 * screen — and they disagree while every one of them still renders a plausible number, which is
	 * the only kind of wrong a dashboard cannot show on its face.
	 *
	 * **The controls are labelled and grouped, and the grouping is half the label.** Eight
	 * dropdowns in a row are eight identical boxes whose meaning has to be inferred from whichever
	 * option happens to be selected — "Every product" and "Everywhere" are the only clue that one is
	 * a product and the next is a country, and they stop being a clue the moment somebody narrows
	 * either. So each field carries a visible label through `FormField`, the same three-part rhythm
	 * every other form on this app uses, and the three groups answer the three questions a filter
	 * bar is actually asked: **what they looked at**, **who they were**, and **where they came
	 * from**. "Product" under the first is unambiguous where "Product" beside "Country" is not.
	 *
	 * **A narrowed control says so on its face.** A dropdown reading "Studio" and one reading
	 * "Every product" are one word apart, so a page filtered to a single product looks exactly like
	 * the whole store — which is the specific way somebody reads one tier's conversion rate as the
	 * catalogue's. Anything not at its "everything" value gets an accent ring, and the count is
	 * stated beside the Clear button.
	 *
	 * The filter is **reflected into the URL**, so a slice can be sent to somebody — which is also why
	 * the two date fields are always on screen rather than behind a Custom button. A saved link may
	 * carry dates no preset matches, and a row of four unselected buttons is not a statement of what
	 * is being drawn; the fields are, and they state it without the bar changing height when anybody
	 * reaches for one.
	 */

	import { createEventDispatcher } from 'svelte';
	import { Button, Toggle } from 'flowbite-svelte';
	import { CurrencyPicker, FormField, SelectField } from '../../../components';
	import { currencyRowsOf } from '../../../stores/storefront';
	import type { AnalyticsFilterOptions, AnalyticsQuery } from '../../../AppTypes';

	export let filter: AnalyticsQuery;
	export let options: AnalyticsFilterOptions | null = null;
	/** Bot visits inside the range, whether or not they are being counted. */
	export let botSessionsExcluded: number = 0;
	export let busy: boolean = false;

	const dispatcher = createEventDispatcher<{ change: AnalyticsQuery }>();

	/**
	 * Preset ranges, as whole days.
	 *
	 * A range control is what an operator actually reaches for; a date pair is what they reach for
	 * once a quarter. The presets lead and the fields sit beside them, both always present — see the
	 * markup for why neither is revealed by the other.
	 */
	const RANGES: { label: string; days: number }[] = [
		{ label: 'Last 7 days', days: 7 },
		{ label: 'Last 30 days', days: 30 },
		{ label: 'Last 90 days', days: 90 },
		{ label: 'Last 12 months', days: 365 }
	];

	/**
	 * The bucket a range of this length should be drawn at.
	 *
	 * A year by day is 365 points in a 280px box, which is a smear rather than a trend. It follows
	 * the range on every change of range — including a custom one, where the operator is most likely
	 * to pick something the default cannot draw.
	 */
	function granularityFor(days: number): 'day' | 'week' | 'month' {
		return days > 180 ? 'month' : days > 45 ? 'week' : 'day';
	}

	function fnRange(days: number): void {
		const to = new Date();
		const from = new Date(to.getTime() - days * 86400000);
		dispatcher('change', {
			...filter,
			from: from.toISOString(),
			to: to.toISOString(),
			granularity: granularityFor(days)
		});
	}

	/**
	 * The custom range, as two native date fields — **always on screen**, never revealed.
	 *
	 * **Native `<input type="date">` rather than a picker component**, and flowbite's `Datepicker` in
	 * particular, which is unusable here on three counts: it injects a stylesheet and a script from
	 * `unpkg.com` at runtime, which is a third-party script on the page that shows the store's
	 * revenue and a hard dependency on the open internet for an admin screen; it exposes **no
	 * binding and no events**, because the value is written by flowbite's vanilla JS straight into
	 * the DOM, so Svelte cannot read what was picked; and it hard-codes `bg-gray-50 dark:bg-gray-700`
	 * against a design system that resolves its own surfaces. The native control gives a real
	 * calendar in every current browser, the OS picker on a phone, full keyboard support and a
	 * locale-formatted display over an ISO value, for no dependency at all.
	 */

	/** `YYYY-MM-DD` in UTC, which is what a date input reads and writes. */
	function dayOf(iso: string | undefined): string {
		if (iso === undefined) return '';
		const parsed = new Date(iso);
		return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10);
	}

	$: fromDay = dayOf(filter.from);
	$: toDay = dayOf(filter.to);
	$: todayDay = new Date().toISOString().slice(0, 10);

	/**
	 * Apply one end of the custom range.
	 *
	 * **The end date is taken to the *end* of its day**, and that is the whole subtlety here:
	 * `new Date('2026-06-30')` is midnight UTC, so a range ending on the 30th that used it would
	 * exclude everything that happened on the 30th — the operator picks a day and the day is missing
	 * from the report, which reads as a data loss rather than as an off-by-one. The start is midnight
	 * for the mirror-image reason. The process is pinned to UTC, so these are the same boundaries the
	 * server buckets on.
	 */
	function fnCustom(edge: 'from' | 'to', day: string): void {
		if (day.length === 0) return;
		const next = {
			...filter,
			[edge]: new Date(`${day}T${edge === 'from' ? '00:00:00.000' : '23:59:59.999'}Z`).toISOString()
		};
		if (new Date(next.from ?? '').getTime() > new Date(next.to ?? '').getTime()) {
			// Cannot happen through the inputs — each bounds the other — but a stale URL can carry it,
			// and a range that runs backwards draws an empty chart with no explanation.
			return;
		}
		const span =
			(new Date(next.to ?? '').getTime() - new Date(next.from ?? '').getTime()) / 86400000;
		dispatcher('change', { ...next, granularity: granularityFor(span) });
	}

	/**
	 * A `<select>`'s new value.
	 *
	 * A helper rather than a cast in the markup: Svelte's template expressions are plain JavaScript,
	 * so a TypeScript `as` there is a parse error rather than a type error — it fails the build
	 * with an "unexpected token" pointing at a column in the middle of an attribute.
	 */
	function fnValueOf(event: Event): string {
		return (event.target as HTMLInputElement).value;
	}

	/**
	 * The same for a `SelectField` or a `CurrencyPicker`, both of which report the row that was
	 * chosen rather than a DOM event.
	 *
	 * Separate from `fnValueOf` rather than one function narrowing between the two: the date fields
	 * beside these are native inputs and still carry their value on the target, and a single helper
	 * reaching for `detail?.value ?? target.value` would answer plausibly for whichever it was
	 * handed — including for a control that had been miswired.
	 */
	function fnChosen(event: CustomEvent<{ value: string }>): string {
		return event.detail.value;
	}

	function fnChecked(event: Event): boolean {
		return (event.target as HTMLInputElement).checked;
	}

	function fnSet(key: keyof AnalyticsQuery, value: string): void {
		dispatcher('change', { ...filter, [key]: value === '' ? undefined : value });
	}

	/**
	 * Which preset is in force, or `null` for a range none of them describes.
	 *
	 * Matched rather than snapped to the nearest. Snapping was fine while presets were the only way
	 * to set a range and is actively misleading now: an operator who picked 1–14 March would see
	 * "Last 7 days" lit, which is a statement about the data on screen and a wrong one. A preset ends
	 * *now*, so a range whose end is not today cannot be one whatever its length.
	 */
	$: activeDays = (() => {
		if (filter.from === undefined || filter.to === undefined) return null;
		const to = new Date(filter.to).getTime();
		const from = new Date(filter.from).getTime();
		if (Number.isNaN(to) || Number.isNaN(from)) return null;
		// A preset's end is the moment it was clicked, so "ends today" is the test rather than
		// equality with `now` — the filter survives in a URL and outlives the click by hours.
		if (new Date(to).toISOString().slice(0, 10) !== todayDay) return null;
		const span = (to - from) / 86400000;
		return RANGES.find(range => Math.abs(range.days - span) < 1)?.days ?? null;
	})();


	/** A `<SelectField>`'s items, with a leading "everything" row that clears the filter. */
	function fnItems(values: string[], anyLabel: string): { value: string; name: string }[] {
		return [{ value: '', name: anyLabel }, ...values.map(value => ({ value, name: value }))];
	}

	/**
	 * The ring on a control that is narrowing something.
	 *
	 * Whole literal class strings per branch, never an interpolated fragment: Tailwind's scanner
	 * reads source text, so a class assembled at runtime is invisible to it and the rule silently
	 * never ships — the same reason `rungColor` returns a hex for an inline style.
	 */
	function fieldClass(active: boolean): string {
		return active ? '!border-primary-500 ring-1 ring-primary-500' : '';
	}

	/**
	 * How many filters are narrowing the page.
	 *
	 * A count rather than a boolean, because "this is not the whole store" is a different statement
	 * from "one thing is filtered", and an operator reading a conversion rate needs to know which.
	 * The range and the bot switch are excluded: every view has a range, and the bot switch states
	 * its own effect on its own line.
	 */
	$: activeFilters = [
		filter.userUUID,
		filter.productUUID,
		filter.familySlug,
		filter.currency,
		filter.country,
		filter.deviceType,
		filter.browser,
		filter.os,
		filter.source,
		filter.medium,
		filter.campaign,
		filter.referrerHost,
		filter.identity === 'any' ? undefined : filter.identity
	].filter(value => value !== undefined && value !== '').length;

	function fnClear(): void {
		dispatcher('change', {
			from: filter.from,
			to: filter.to,
			granularity: filter.granularity,
			includeBots: filter.includeBots
		});
	}
</script>

<div class="widget bg-depth space-y-4">
	<!-- When ────────────────────────────────────────────────────────────────────────────── -->
	<!--
		The presets and the exact dates sit on **one row, both always present**. The dates used to be
		revealed by a Custom button, which pushed the three filter groups and every panel below them
		down the page the moment somebody reached for a date — so the control you were aiming at moved
		as you opened it, and the chart you were reading moved with it. A filter bar is the one thing
		on the screen that must not resize when it is used.

		It also removes a control: the fields **are** the custom range, so choosing one is choosing it,
		and there is no mode to be in. They double as the statement of what is being drawn, which is
		why the range is no longer written out underneath — one fact, stated once.
	-->
	<div class="flex flex-wrap items-end gap-x-4 gap-y-2">
		<div class="min-w-0">
			<div class="card-label mb-1.5">
				<i class="fa-solid fa-calendar-days text-[11px]" />Period
			</div>
			<div class="flex flex-wrap items-center gap-1.5">
				{#each RANGES as range (range.days)}
					<Button
						size="xs"
						color={activeDays === range.days ? 'primary' : 'alternative'}
						disabled={busy}
						on:click={() => fnRange(range.days)}
					>
						{range.label}
					</Button>
				{/each}
			</div>
		</div>

		<!--
			Each field bounds the other, and neither may pass today: a range that runs backwards draws
			an empty chart with nothing on screen saying why, and one ending in the future draws a flat
			tail that reads as traffic having stopped.
		-->
		<div class="flex items-end gap-2">
			<div>
				<label class="card-label mb-1" for="analytics-from">From</label>
				<input
					id="analytics-from"
					type="date"
					class="border border-input bg-depth-current rounded-lg text-sm p-1.5 focus:border-primary-500 focus:ring-primary-500"
					value={fromDay}
					max={toDay.length > 0 ? toDay : todayDay}
					disabled={busy}
					on:change={event => fnCustom('from', fnValueOf(event))}
				/>
			</div>
			<span class="text-default text-xs pb-2">to</span>
			<div>
				<label class="card-label mb-1" for="analytics-to">To</label>
				<input
					id="analytics-to"
					type="date"
					class="border border-input bg-depth-current rounded-lg text-sm p-1.5 focus:border-primary-500 focus:ring-primary-500"
					value={toDay}
					min={fromDay}
					max={todayDay}
					disabled={busy}
					on:change={event => fnCustom('to', fnValueOf(event))}
				/>
			</div>
		</div>

		<div class="ml-auto w-32">
			<FormField label="Group by" id="analytics-granularity">
				<SelectField
					id="analytics-granularity"
					size="sm"
					value={filter.granularity ?? 'day'}
					on:change={event => fnSet('granularity', fnChosen(event))}
					items={[
						{ value: 'day', name: 'Daily' },
						{ value: 'week', name: 'Weekly' },
						{ value: 'month', name: 'Monthly' }
					]}
				/>
			</FormField>
		</div>
	</div>

	<!-- What ────────────────────────────────────────────────────────────────────────────── -->
	<div class="pt-3 border-t border-widget-color">
		<div class="card-label mb-2"><i class="fa-solid fa-box-open text-[11px]" />What they looked at</div>
		<div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
			<FormField label="Product family" id="analytics-family">
				<SelectField
					id="analytics-family"
					size="sm"
					class={fieldClass(filter.familySlug !== undefined)}
					value={filter.familySlug ?? ''}
					on:change={event => fnSet('familySlug', fnChosen(event))}
					items={[
						{ value: '', name: 'Every family' },
						...(options?.families ?? []).map(family => ({
							value: family.slug,
							name: family.name
						}))
					]}
				/>
			</FormField>

			<FormField label="Product" id="analytics-product">
				<SelectField
					id="analytics-product"
					size="sm"
					class={fieldClass(filter.productUUID !== undefined)}
					value={filter.productUUID ?? ''}
					on:change={event => fnSet('productUUID', fnChosen(event))}
					items={[
						{ value: '', name: 'Every product' },
						...(options?.products ?? []).map(product => ({
							value: product.uuid,
							name: product.name
						}))
					]}
				/>
			</FormField>

			<FormField
				label="Currency shown"
				id="analytics-currency"
				helpText="The currency the storefront was pricing in when they looked, not the one they were eventually billed in — those differ for anyone who switched before buying."
			>
				<CurrencyPicker
					id="analytics-currency"
					label="Currency shown"
					compact
					class={fieldClass(filter.currency !== undefined)}
					currencies={$currencyRowsOf(options?.currencies ?? [])}
					value={filter.currency ?? ''}
					anyLabel="Every currency"
					on:change={event => fnSet('currency', fnChosen(event))}
				/>
			</FormField>
		</div>
	</div>

	<!-- Who ─────────────────────────────────────────────────────────────────────────────── -->
	<div class="pt-3 border-t border-widget-color">
		<div class="card-label mb-2"><i class="fa-solid fa-users text-[11px]" />Who they were</div>
		<div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
			<FormField
				label="Country"
				id="analytics-country"
				helpText="Taken from the CDN's country header where the deployment has one, or from the customer's billing country once they are known. No location database ships with the store, so a visit with neither is Unknown rather than guessed."
			>
				<SelectField
					id="analytics-country"
					size="sm"
					class={fieldClass(filter.country !== undefined)}
					value={filter.country ?? ''}
					on:change={event => fnSet('country', fnChosen(event))}
					items={fnItems(options?.countries ?? [], 'Everywhere')}
				/>
			</FormField>

			<FormField label="Device" id="analytics-device">
				<SelectField
					id="analytics-device"
					size="sm"
					class={fieldClass(filter.deviceType !== undefined)}
					value={filter.deviceType ?? ''}
					on:change={event => fnSet('deviceType', fnChosen(event))}
					items={fnItems(options?.devices ?? [], 'Every device')}
				/>
			</FormField>

			<FormField
				label="Account"
				id="analytics-identity"
				helpText="Whether the store knew who they were. Most of a converting journey happens before anyone signs in, so a visit that ends in a purchase counts as signed in from the moment they did."
			>
				<SelectField
					id="analytics-identity"
					size="sm"
					class={fieldClass(filter.identity !== undefined && filter.identity !== 'any')}
					value={filter.identity ?? 'any'}
					on:change={event => fnSet('identity', fnChosen(event))}
					items={[
						{ value: 'any', name: 'Signed in or not' },
						{ value: 'known', name: 'Signed in only' },
						{ value: 'anonymous', name: 'Not signed in only' }
					]}
				/>
			</FormField>
		</div>
	</div>

	<!-- Where from ──────────────────────────────────────────────────────────────────────── -->
	<div class="pt-3 border-t border-widget-color">
		<div class="card-label mb-2">
			<i class="fa-solid fa-signs-post text-[11px]" />Where they came from
		</div>
		<div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
			<FormField
				label="Traffic source"
				id="analytics-source"
				helpText="The utm_source on the link they first arrived through. A visit with none is a direct visit or an untagged link, and appears as Unknown in the acquisition report."
			>
				<SelectField
					id="analytics-source"
					size="sm"
					class={fieldClass(filter.source !== undefined)}
					value={filter.source ?? ''}
					on:change={event => fnSet('source', fnChosen(event))}
					items={fnItems(options?.sources ?? [], 'Every source')}
				/>
			</FormField>

			<FormField label="Campaign" id="analytics-campaign">
				<SelectField
					id="analytics-campaign"
					size="sm"
					class={fieldClass(filter.campaign !== undefined)}
					value={filter.campaign ?? ''}
					on:change={event => fnSet('campaign', fnChosen(event))}
					items={fnItems(options?.campaigns ?? [], 'Every campaign')}
				/>
			</FormField>

			<FormField label="Referring site" id="analytics-referrer">
				<SelectField
					id="analytics-referrer"
					size="sm"
					class={fieldClass(filter.referrerHost !== undefined)}
					value={filter.referrerHost ?? ''}
					on:change={event => fnSet('referrerHost', fnChosen(event))}
					items={fnItems(options?.referrers ?? [], 'Everywhere')}
				/>
			</FormField>
		</div>
	</div>

	{#if filter.userUUID !== undefined}
		<!--
			A customer filter arrives as a deep link from the support page rather than from a control
			here, so it is stated rather than offered: a filter in force with nothing on screen naming
			it is a page reporting one person's behaviour as the store's.
		-->
		<div
			class="flex flex-wrap items-center gap-2 text-sm pt-3 border-t border-widget-color"
		>
			<i class="fa-solid fa-user text-default text-xs" />
			<span>Showing one customer's visits and purchases.</span>
			<Button size="xs" color="alternative" on:click={() => fnSet('userUUID', '')}>
				Show everyone
			</Button>
		</div>
	{/if}

	<!-- Bots, and the state of the whole bar ────────────────────────────────────────────── -->
	<div class="flex flex-wrap items-center gap-x-4 gap-y-2 pt-3 border-t border-widget-color">
		<Toggle
			checked={filter.includeBots === true}
			on:change={event =>
				dispatcher('change', { ...filter, includeBots: fnChecked(event) })}
		>
			<span class="text-sm">Count crawlers and bots</span>
		</Toggle>
		<!--
			The count is stated whether the switch is on or off. A funnel that silently drops most of
			its traffic is a conversion rate nobody can check, and a funnel that silently counts it
			is a conversion rate of about a third of a per cent.
		-->
		<span class="text-xs text-default">
			{#if botSessionsExcluded === 0}
				No bot visits in this range.
			{:else if filter.includeBots === true}
				{botSessionsExcluded.toLocaleString()} bot visits are being counted.
			{:else}
				{botSessionsExcluded.toLocaleString()} bot visits are being held back.
			{/if}
		</span>

		<div class="ml-auto flex items-center gap-2">
			{#if activeFilters > 0}
				<!-- Named rather than implied: a figure read off a filtered page and quoted as the
				     store's is the mistake the ring on each control is also there to prevent. -->
				<span class="text-xs text-primary-600 dark:text-primary-400">
					{activeFilters}
					{activeFilters === 1 ? 'filter' : 'filters'} applied
				</span>
				<Button size="xs" color="alternative" on:click={fnClear}>Clear filters</Button>
			{:else}
				<span class="text-xs text-default">Showing the whole store.</span>
			{/if}
		</div>
	</div>
</div>
